// @flow
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';

import Filters from '@homeaway/react-search-filters';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import FormModal from '../FormModal/FormModal';

import {media} from '../../utils/breakpoints';
import withLingua from '../withLingua/withLingua';

import routerActions from '../../actions/router';
import overlayActions from '../../actions/overlay';
import searchParamsActions from '../../actions/searchParams';
import {FILTERS_MODAL} from '../../actions/modals';

import type {State as ReduxState} from '../../reducers/index';
import type {Action as OverlayAction} from '../../actions/overlay';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '@homeaway/react-search-filters';
import type {LinguaProps} from '../withLingua/withLingua';
import type {State as AbacusState} from '../../reducers/abacus';

import {
    instantBookFilter,
    isOnlyInstantConfirmationFilter
} from '../../utils/filters';

export type FilterDispatchProps = {
    filter: typeof routerActions.filter,
    onDone: () => void,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity: typeof searchParamsActions.updatePriceInputValuesValidity
};

export type FiltersProps = {
    abacus: AbacusState,
    currency: string,
    hasDates: boolean,
    inputParamsAreChanged: boolean,
    inputParamsAreDefault: boolean,
    isMobile: boolean,
    locale: string,
    numFiltersPanel?: ?number,
    priceInputValuesAreValid: boolean,
    viewportHeight: number
};

export type FilterFormProps = {
    filterGroups: FilterGroupType[],
    inputParams: SearchParamsType,
    modalIsOpen: boolean,
    resultsCount?: ?number
} & FiltersProps &
    FilterDispatchProps;

export type FilterBarProps = {
    currentBedrooms: number,
    exactBedroomsMatch: boolean,
    hideOverlay: () => OverlayAction,
    inputMaxBedrooms: number,
    inputMaxPrice: number,
    inputMinBedrooms: number,
    inputMinPrice: number,
    isInstantBookingChecked: boolean,
    isMoreFiltersSelected: boolean,
    isOverlayVisible: boolean,
    maxBedrooms: number,
    maxPrice: number,
    minBedrooms: number,
    minPrice: number,
    searchResultsLoading: boolean,
    showOverlay: () => OverlayAction,
    siteName: string,
    topBannersHeight: number
} & FiltersProps &
    FilterDispatchProps;

export type FilterContainerProps = FilterBarProps &
    FilterFormProps &
    FilterDispatchProps &
    LinguaProps;

export function FiltersContainer(props: FilterContainerProps) {
    return (
        <Filters
            {...props}
            filterCountDataProvider={FilteredCountDataProvider}
            modal={FormModal}
        />
    );
}

export const mapStateToProps = (state: ReduxState) => {
    // select params that only apply to this component
    const {
        abacus,
        modals,
        overlay: {showOverlay: isOverlayVisible},
        searchParams: {
            inputParams,
            inputParamsAreChanged,
            inputParamsAreDefault,
            numFiltersPanel,
            params,
            params: {
                filter,
                maxBathrooms,
                maxBedrooms,
                maxPrice,
                minBathrooms,
                minBedrooms,
                minPrice
            },
            priceInputValuesAreValid,
            selectedFilters
        },
        system: {currency, siteName, fullLocale},
        topBanners: {height: topBannersHeight},
        viewport: {height, breakpoint}
    } = state;
    return {
        abacus,
        bedroomsExactMatch: inputParams.minBedrooms === inputParams.maxBedrooms,
        canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
        canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
        currency,
        hasDates: !!(inputParams.arrival && inputParams.departure),
        inputMaxBedrooms: inputParams.maxBedrooms,
        inputMaxPrice: inputParams.maxPrice,
        inputMinBedrooms: inputParams.minBedrooms,
        inputMinPrice: inputParams.minPrice,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreDefault,
        isInstantBookingChecked: !!instantBookFilter(filter),
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
        isMoreFiltersSelected: !!(
            (minBathrooms && minBathrooms > 0) ||
            (maxBathrooms && maxBathrooms > 0) ||
            (filter &&
                filter.length > 0 &&
                !isOnlyInstantConfirmationFilter(filter))
        ),
        isOverlayVisible,
        locale: fullLocale,
        maxBedrooms,
        maxPrice,
        minBedrooms,
        minPrice,
        modalIsOpen: modals.FILTERS.isOpen,
        numFiltersPanel,
        params,
        priceInputValuesAreValid,
        selectedFilters,
        siteName,
        topBannersHeight,
        viewportHeight: height
    };
};

export const mapDispatchToProps = {
    filter: routerActions.filter,
    hideOverlay: () => overlayActions.toggleOverlay(false),
    onDone: () => routerActions.popModal(FILTERS_MODAL),
    resetInputParams: searchParamsActions.resetInputParams,
    showOverlay: () => overlayActions.toggleOverlay(true),
    syncInputParams: searchParamsActions.syncInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    updateInputParams: searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity:
        searchParamsActions.updatePriceInputValuesValidity
};

const ConnectedFiltersContainer = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withLingua({
        namespace: 'traveler/search',
        category: 'messages'
    })
)(FiltersContainer);

export default ConnectedFiltersContainer;
